import {create} from 'zustand';
import {authService} from '@/services/auth.service';
import {syncService} from '@/services/sync.service';
import {storage} from '@/services/storage';
import type {User, EmailAccount} from '@/types';

interface UserState {
  /** Auth */
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  authLoading: boolean;
  authError: string | null;

  /** Onboarding */
  isOnboarded: boolean;
  selectedCategories: string[];

  /** Email sync */
  connectedEmail: string | null;
  emailAccounts: EmailAccount[];
  syncStatus: 'idle' | 'syncing' | 'done' | 'error';
  syncProgress: number;
  syncItemCount: number;
}

interface UserActions {
  /** Auth actions */
  signInWithGoogle: (code: string) => Promise<void>;
  linkGoogleEmail: (code: string) => Promise<void>;
  restoreSession: () => Promise<void>;
  signOut: () => void;

  /** Onboarding */
  setSelectedCategories: (categories: string[]) => void;
  toggleCategory: (category: string) => void;
  completeOnboarding: () => void;

  /** Sync */
  setConnectedEmail: (email: string | null) => void;
  setSyncStatus: (status: UserState['syncStatus']) => void;
  setSyncProgress: (progress: number, itemCount: number) => void;
  pollSyncProgress: () => Promise<void>;
  logout: () => void;
}

const initialState: UserState = {
  isAuthenticated: false,
  user: null,
  token: null,
  authLoading: false,
  authError: null,
  isOnboarded: false,
  selectedCategories: ['Fashion', 'Kids', 'Beauty'],
  connectedEmail: null,
  emailAccounts: [],
  syncStatus: 'idle',
  syncProgress: 0,
  syncItemCount: 0,
};

export const useUserStore = create<UserState & UserActions>()((set, get) => ({
  ...initialState,

  signInWithGoogle: async (code: string) => {
    set({authLoading: true, authError: null});
    try {
      console.log("SIGN IN WITH GOOGLE")
      const {token, user} = await authService.signInWithGoogle(code);
      console.log("USER: ", user)
      storage.setUser(user);
      set({isAuthenticated: true, user, token, authLoading: false});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      set({authLoading: false, authError: message});
    }
  },

  linkGoogleEmail: async (code: string) => {
    set({syncStatus: 'syncing', syncProgress: 0, syncItemCount: 0});
    try {
      const response = await authService.linkGoogleEmail(code);
      // Update user/token if the server returns them
      if (response.token) {
        storage.setToken(response.token);
        set({token: response.token});
      }
      if (response.user) {
        storage.setUser(response.user);
        set({user: response.user});
      }
      // Fetch email accounts to confirm link
      const accounts = await syncService.getEmailAccounts();
      const gmailAccount = accounts.find((a) => a.provider === 'google') ?? accounts[0];
      set({
        connectedEmail: gmailAccount?.email ?? 'gmail',
        emailAccounts: accounts,
      });
      // Start polling for sync progress
      get().pollSyncProgress();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Email sync failed';
      set({syncStatus: 'error', authError: message});
    }
  },

  restoreSession: async () => {
    const token = storage.getToken();
    if (!token) return;

    set({authLoading: true});
    try {
      const user = await authService.getMe();
      storage.setUser(user);
      set({
        isAuthenticated: true,
        user,
        token,
        isOnboarded: true,
        authLoading: false,
      });
      // Also fetch email accounts
      try {
        const accounts = await syncService.getEmailAccounts();
        if (accounts.length > 0) {
          set({
            emailAccounts: accounts,
            connectedEmail: accounts[0]?.email ?? null,
          });
        }
      } catch {
        // Non-critical — account may not have linked email yet
      }
    } catch {
      // Token is invalid / expired
      storage.clear();
      set({authLoading: false});
    }
  },

  signOut: () => {
    authService.signOut();
    set(initialState);
  },

  setSelectedCategories: (categories) => {
    set({selectedCategories: categories});
  },

  toggleCategory: (category) => {
    set((state) => {
      const exists = state.selectedCategories.includes(category);
      return {
        selectedCategories: exists
          ? state.selectedCategories.filter((c) => c !== category)
          : [...state.selectedCategories, category],
      };
    });
  },

  completeOnboarding: () => {
    set({isOnboarded: true});
  },

  setConnectedEmail: (email) => {
    set({connectedEmail: email});
  },

  setSyncStatus: (status) => {
    set({syncStatus: status});
  },

  setSyncProgress: (progress, itemCount) => {
    set({syncProgress: progress, syncItemCount: itemCount});
  },

  pollSyncProgress: async () => {
    const poll = async () => {
      try {
        const tasks = await syncService.getTasks();
        const activeTask = tasks.find(
          (t) => t.status === 'in_progress' || t.status === 'pending',
        );
        if (activeTask) {
          const total = activeTask.total || 1;
          const pct = Math.round((activeTask.progress / total) * 100);
          set({syncProgress: pct, syncItemCount: activeTask.progress});

          if (activeTask.status === 'in_progress') {
            setTimeout(poll, 2000);
          }
        } else {
          // No active tasks — check if any completed
          const completedTask = tasks.find((t) => t.status === 'completed');
          if (completedTask) {
            const total = completedTask.total || completedTask.progress;
            set({
              syncStatus: 'done',
              syncProgress: 100,
              syncItemCount: total,
            });
          } else if (tasks.length === 0) {
            // No tasks yet — might not have started, poll again briefly
            setTimeout(poll, 3000);
          }
        }
      } catch {
        // If polling fails, just mark done to unblock the user
        set({syncStatus: 'done', syncProgress: 100});
      }
    };

    // Start polling after a short delay to let the server create the task
    setTimeout(poll, 2000);
  },

  logout: () => {
    authService.signOut();
    set(initialState);
  },
}));
